/**
 * The flow an Integration Flow menu item (nav obj_type "J") runs — picked
 * from the IOFlow flows the project owner can see, the project's own first.
 * Stored as `{"flow_id": …}` in the node's `functional_specs` (the same place
 * an External Website keeps its slug). docs/flow_forms.md.
 */
import { useEffect, useMemo, useState } from 'react'
import { listAvailableFlows, type AvailableFlow } from '@/api/flowForms'

export function flowIdFromSpecs(raw: string | undefined): string {
  try {
    const spec = JSON.parse(raw || '{}')
    return spec && typeof spec.flow_id === 'string' ? spec.flow_id : ''
  } catch {
    return ''
  }
}

export function slugForObjName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
    || 'flow'
}

const TRIGGER_LABEL: Record<string, string> = {
  manual: 'by hand — it gets an input form',
  schedule: 'scheduled',
  event: 'on an application event',
  application_event: 'on an application event',
  webhook: 'on a webhook',
  api_call: 'on an API call',
  chat_input: 'chat',
}

export default function FlowNodePicker({ project, rawSpecs, originalFlowId, onPick }: {
  project: string
  rawSpecs: string
  /** The flow the saved node pointed at (to warn when it changes). */
  originalFlowId: string
  onPick: (flow: AvailableFlow) => void
}) {
  const [flows, setFlows] = useState<AvailableFlow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const flowId = flowIdFromSpecs(rawSpecs)

  useEffect(() => {
    let cancelled = false
    listAvailableFlows(project)
      .then((r) => { if (!cancelled) setFlows(r.flows || []) })
      .catch((e: unknown) => {
        if (cancelled) return
        const err = e as { response?: { data?: { detail?: string } } }
        setError(err?.response?.data?.detail || 'IOFlow did not list its flows')
        setFlows([])
      })
    return () => { cancelled = true }
  }, [project])

  const selected = useMemo(() => flows?.find((f) => f.flow_id === flowId) || null, [flows, flowId])
  const own = (flows || []).filter((f) => f.project_flow)
  const others = (flows || []).filter((f) => !f.project_flow)

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
        IOFlow flow *
      </label>
      <select
        value={flowId}
        disabled={flows === null}
        data-tw-field="flow_id"
        onChange={(e) => {
          const f = (flows || []).find((x) => x.flow_id === e.target.value)
          if (f) onPick(f)
        }}
        style={{
          width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)',
          fontSize: '0.9375rem',
        }}
      >
        <option value="">{flows === null ? 'Loading flows…' : 'Choose a flow…'}</option>
        {flowId && !selected && flows !== null && (
          <option value={flowId}>{flowId} (not visible in IOFlow)</option>
        )}
        {own.length > 0 && (
          <optgroup label="This application's flows">
            {own.map((f) => <option key={f.flow_id} value={f.flow_id}>{f.name}</option>)}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label="Other flows in IOFlow">
            {others.map((f) => <option key={f.flow_id} value={f.flow_id}>{f.name}</option>)}
          </optgroup>
        )}
      </select>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
        {error ? <span style={{ color: 'var(--color-error)' }}>{error}</span>
          : selected ? (
            <>
              Starts {TRIGGER_LABEL[selected.trigger_type] || selected.trigger_type || 'by hand'}
              {selected.status && selected.status.toLowerCase() !== 'active' ? ` · ${selected.status}` : ''}.
              {selected.trigger_type === 'manual' && ' Saving makes its input form — an entry screen you can design like any other (look-ups, item change, validations).'}
            </>
          ) : 'The menu item opens this flow: a form to run it, its runs and their results.'}
        {originalFlowId && flowId && originalFlowId !== flowId && (
          <div style={{ color: 'var(--color-warning)', marginTop: 4 }}>
            This item ran another flow before. Saving replaces its input form with one for this flow.
          </div>
        )}
      </div>
    </div>
  )
}
